import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThemeToggle from "./ThemeToggle";
import { useLogoutMutation, useProfileQuery } from "../features/auth/authApi";
import { PartnerApi } from "../api/partnerApi";
import { getClientToken } from "../helper/clientAuth";

const HeaderOne = ({
  setOpenGetDemoModal,
  setOpenLoginModal,
  setOpenRegisterModal,
  isAuthenticated,
  isPartnerDashboard = false,
  onDashboardMenuToggle,
  dashboardMenu = [],
}) => {
  const { t, i18n } = useTranslation();
  const [scroll, setScroll] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const { data: profileResponse } = useProfileQuery(undefined, { skip: !isAuthenticated });
  const [hasPartnerApplication, setHasPartnerApplication] = useState(false);
  const profileUser = profileResponse?.data?.user || profileResponse?.user || profileResponse?.data || {};

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset < 150) {
        setScroll(false);
      } else {
        setScroll(true);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !profileUser.email) {
      setHasPartnerApplication(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const login = await PartnerApi.post("/api/v1/auth/shared-login", { source: "payways", token: getClientToken(), email: profileUser.email, product_slug: "payways", remember_me: false });
        const accessToken = login.data?.data?.tokens?.access_token;
        if (!accessToken) return;
        const response = await PartnerApi.get("/api/v1/applications", { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!cancelled) setHasPartnerApplication((response.data?.data?.items || []).length > 0);
      } catch {
        if (!cancelled) setHasPartnerApplication(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, profileUser.email]);

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang);

    const segments = location.pathname.split("/");
    segments[1] = lang; // replace language segment
    const newPath = segments.join("/");

    navigate(newPath, { replace: true });
  };
  // const moveHomeClick = (event, href) => {
  //   event.preventDefault();
  //   if (location.pathname !== `/${i18n.language}`) {
  //     navigate(`/${i18n.language}`, { replace: false });
  //     setTimeout(() => {
  //       const element = document.querySelector(href);
  //       if (element) element.scrollIntoView({ behavior: 'smooth' });
  //     }, 100);
  //   } else {
  //     const element = document.querySelector(href);
  //     if (element) element.scrollIntoView({ behavior: 'smooth' });
  //   }
  // };

  const moveHomeClick = (event, href) => {
    event.preventDefault();

    const homePath = i18n.language === "hy" ? "/" : `/${i18n.language}`;

    if (location.pathname === homePath) {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
      return;
    }

    navigate(homePath, { replace: false });

    const scrollToAnchor = () => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.removeEventListener("popstate", scrollToAnchor);
      }
    };

    setTimeout(scrollToAnchor, 200);

    window.addEventListener("popstate", scrollToAnchor);
  };

  const handleLogout = async () => {
    try {
      await logout().unwrap();
    } catch (error) {
      console.error("Logout request failed", error?.data || error?.message || error);
    } finally {
      navigate(i18n.language === "hy" ? "/" : `/${i18n.language}`, { replace: true });
    }
  };

  return (
    <>
      <div className="overlay"></div>
      {/* ==================== Header Start Here ==================== */}
      <header className={`header ${scroll ? "fixed-header" : ""} ${isMobileMenuOpen ? "mobile-menu-open" : ""}`}>
        <div className="container container-full">
          <nav className="header-inner flx-between">
            {/* Logo Start */}
            <div className="logo">
              <Link
                to={`/${i18n.language}`}
                className="link white-version"
              >
                <img src="/assets/images/logo/logo-two.png" alt="Logo" />
              </Link>
              <Link
                to={`/${i18n.language}`}
                className="link dark-version"
              >
                <img src="/assets/images/logo/white-logo.png" alt="Logo" />
              </Link>
            </div>
            {/* Logo End  */}
            {/* Menu Start  */}
            <div className="header-menu d-lg-block d-none">
              <ul className="nav-menu flx-align">
                <li className="nav-menu__item">
                  <a
                    className="nav-menu__link"
                    href="#about"
                    onClick={(event) => moveHomeClick(event, "#about")}
                  >
                    {t("about")}
                  </a>
                </li>
                <li className="nav-menu__item">
                  <a
                    className="nav-menu__link"
                    href="#solutions"
                    onClick={(event) => moveHomeClick(event, "#solutions")}
                  >
                    {t("solutions")}
                  </a>
                </li>

                {/* <li className="nav-menu__item">
                  <NavLink to={`/${i18n.language}/payment`} className="nav-menu__link">
                    {t('payment')}
                  </NavLink>
                </li> */}

                <li className="nav-menu__item">
                  <NavLink
                    to={`/${i18n.language}/contact`}
                    className="nav-menu__link"
                  >
                    {t("contact")}
                  </NavLink>
                </li>
                <li className="nav-menu__item">
                  <NavLink to={`/${i18n.language}/partnership`} className="nav-menu__link">
                    {t("partnership.nav", "Գործընկերություն")}
                  </NavLink>
                </li>
              </ul>
            </div>
            {/* Menu End  */}
            {/* Header Right start */}
            <div className="header-right flx-align">

              {/* Light Dark Mode */}
              <ThemeToggle />
              <div className={`gap-3 flx-align d-flex header-auth-actions ${isAuthenticated ? "is-authenticated" : ""}`}>
                {isAuthenticated ? (
                  <div className="header-account-menu" ref={accountMenuRef}>
                    <button type="button" className="btn btn-main pill header-action-button" onClick={() => { setIsMobileMenuOpen(false); setIsAccountMenuOpen((open) => !open); }} aria-expanded={isAccountMenuOpen}>
                      <i className="las la-user header-account-button-icon " aria-hidden="true" />
                      {t("profileMenu.account", "Անձնական էջ")} <i className="las la-angle-down ms-2" aria-hidden="true" />
                    </button>
                    {isAccountMenuOpen && <div className="header-account-dropdown">
                      <button className="header-account-item active" type="button" onClick={() => { setIsAccountMenuOpen(false); navigate(i18n.language === "hy" ? "/profile" : `/${i18n.language}/profile`); }}><i className="las la-user-circle" /><span><strong>{t("profileMenu.profile", "Անձնական էջ")}</strong><small>{t("profileMenu.subtitle", "Տվյալներ և կարգավորումներ")}</small></span></button>
                      {hasPartnerApplication && <div className="header-account-section-title"><i className="las la-users" /><strong>{t("profileMenu.partner", "Գործընկեր")}</strong></div>}
                      {hasPartnerApplication && <button className="header-account-item header-account-item--activity" type="button" onClick={() => { setIsAccountMenuOpen(false); navigate(`/${i18n.language}/partner/activity`); }}><i className="las la-clipboard-list" /><span><strong>{t("profileMenu.activity", "Իմ ակտիվությունը")}</strong><small style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{t("profileMenu.activitySubtitle", "Գործընկերային ծրագրի ակտիվություն")}</small></span></button>}
                      <button className="header-account-item" type="button" onClick={handleLogout} disabled={isLoggingOut}><i className="las la-sign-out-alt" />{isLoggingOut ? "..." : t("profilePage.exit", "Ելք")}</button>
                    </div>}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-main pill header-action-button"
                      onClick={() => setOpenLoginModal(true)}
                    >
                      {t("login")}
                    </button>

                    <button
                      type="button"
                      className="btn btn-main pill header-action-button"
                      onClick={() => setOpenRegisterModal(true)}
                    >
                      {t("register")}
                    </button>
                  </>
                )}
              </div>
              {/* Light Dark Mode */}
              <div className="header-right__inner gap-3 flx-align d-lg-flex d-none">
                <button
                  id="demo-button-header"
                  onClick={() => navigate(`/${i18n.language}/payment`)}
                  className="btn btn-main pill header-action-button"
                >
                  <span className="icon-left icon">
                    <img
                      src="/assets/images/icons/coins-hand.svg"
                      alt="coins"
                    />
                  </span>
                  {t("makePayment")}
                </button>
                {/* <div className="language-select d-flex align-items-center select-has-icon">
                  <img src={`/assets/images/thumbs/${i18n.language}-flag.png`} alt="" className="lng-icon" />

                  <div className="select py-0 ps-2 border-0 fw-500">{i18n.language === 'en' ? t('language.english') : t('language.armenian')}
                    <ul>
                      <li onClick={() => handleChangeLanguage('en')}>{t('language.english')}</li>
                      <li onClick={() => handleChangeLanguage('hy')}>{t('language.armenian')}</li>
                    </ul>
                  </div>
                </div> */}
              </div>
              <button
                type="button"
                className="btn btn-main pill mobile-payment-button"
                onClick={() => navigate(`/${i18n.language}/payment`)}
              >
                <span className="icon-left icon">
                  <img src="/assets/images/icons/coins-hand.svg" alt="coins" />
                </span>
                {t("makePayment")}
              </button>
              <button
                type="button"
                className="mobile-menu-toggle"
                aria-label="Open menu"
                aria-expanded={isMobileMenuOpen}
                onClick={() => isPartnerDashboard ? setIsMobileMenuOpen((open) => !open) : setIsMobileMenuOpen((open) => !open)}
              >
                <i className={`las ${isMobileMenuOpen ? "la-times" : "la-bars"}`} />
              </button>
            </div>
            {/* Header Right End  */}
          </nav>
        </div>
      </header>
      <div className={`mobile-header-menu ${isMobileMenuOpen ? "is-open" : ""}`}>
        <div className="mobile-header-menu__top">
          <button type="button" aria-label="Close menu" onClick={() => setIsMobileMenuOpen(false)}><i className="las la-times" /></button>
        </div>
        <nav className="mobile-header-links">
          {isPartnerDashboard ? dashboardMenu.map(([id, icon, label]) => <button key={id} type="button" className={`mobile-header-dashboard-item ${location.pathname.endsWith(`/${id}`) ? "is-active" : ""}`} onClick={() => { setIsMobileMenuOpen(false); onDashboardMenuToggle?.(id); }}>{label}</button>) : <>
          <a href="#about" onClick={(event) => { moveHomeClick(event, "#about"); setIsMobileMenuOpen(false); }}>{t("about")}</a>
          <a href="#solutions" onClick={(event) => { moveHomeClick(event, "#solutions"); setIsMobileMenuOpen(false); }}>{t("solutions")}</a>
          <NavLink to={`/${i18n.language}/contact`} onClick={() => setIsMobileMenuOpen(false)}>{t("contact")}</NavLink>
          <NavLink to={`/${i18n.language}/partnership`} onClick={() => setIsMobileMenuOpen(false)}>{t("partnership.nav", "Գործընկերություն")}</NavLink>
          {!isAuthenticated && <>
            <div className="mobile-header-auth">
              <button type="button" onClick={() => { setIsMobileMenuOpen(false); setOpenLoginModal(true); }}><i className="las la-user" />{t("login")}</button>
              <button type="button" onClick={() => { setIsMobileMenuOpen(false); setOpenRegisterModal(true); }}><i className="las la-user-plus" />{t("register")}</button>
            </div>
          </>}
          {isAuthenticated && <NavLink to={i18n.language === "hy" ? "/profile" : `/${i18n.language}/profile`} onClick={() => setIsMobileMenuOpen(false)}>{t("profileMenu.profile", "Անձնական էջ")}</NavLink>}
          <button className="mobile-header-payment" type="button" onClick={() => { setIsMobileMenuOpen(false); navigate(`/${i18n.language}/payment`); }}><i className="las la-receipt" />{t("makePayment")}</button></>}
        </nav>
      </div>
      {/* ==================== Header End Here ==================== */}
    </>
  );
};

export default HeaderOne;
